import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import DreamJournal from "@/pages/DreamJournal";
import DreamsLibrary from "@/pages/DreamsLibrary";
import DreamDetail from "@/pages/DreamDetail";
import PremiumPage from "@/pages/PremiumPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import CommunityFeed from "@/pages/CommunityFeed";
import DreamCircles from "@/pages/DreamCircles";
import CommunityHub from "@/pages/CommunityHub";
import Messages from "@/pages/Messages";
import CircleDetail from "@/pages/CircleDetail";
import ResetPassword from "@/pages/ResetPassword";
import AdminPage from "@/pages/AdminPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import BlogPage from "@/pages/BlogPage";
import BlogArticle from "@/pages/BlogArticle";
import ContactPage from "@/pages/ContactPage";
import RelaxationPage from "@/pages/RelaxationPage";
import SettingsPage from "@/pages/SettingsPage";
import DreamGallery from "@/pages/DreamGallery";
import GamesPage from "@/pages/GamesPage";
import MyArtworks from "@/pages/MyArtworks";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import AppVersion from "@/components/AppVersion";
import "@/App.css";

// Register Service Worker for PWA - force update on new version
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('SW registered:', registration.scope);
        // Check for updates immediately and every 60 seconds
        registration.update();
        setInterval(() => registration.update(), 60000);
        // Auto-activate new service worker
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'activated') {
                window.location.reload();
              }
            });
          }
        });
      })
      .catch((error) => {
        console.log('SW registration failed:', error);
      });
  });
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const { t } = useTranslation();
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [dreamMode, setDreamMode] = useState(localStorage.getItem('dreamMode') === 'true');

  // Apply dream mode to body with inline style for guaranteed override
  useEffect(() => {
    if (dreamMode) {
      document.body.classList.add('dream-mode');
      document.documentElement.style.background = 'linear-gradient(160deg, #2a1845 0%, #1f1235 40%, #251840 100%)';
      document.body.style.background = 'transparent';
      document.body.style.minHeight = '100vh';
    } else {
      document.body.classList.remove('dream-mode');
      document.documentElement.style.background = '';
      document.body.style.background = '';
      document.body.style.minHeight = '';
    }
    localStorage.setItem('dreamMode', dreamMode);
  }, [dreamMode]);

  const toggleDreamMode = () => {
    setDreamMode(!dreamMode);
  };

  // Update page title based on language
  useEffect(() => {
    document.title = t('siteTitle');
  }, [t]);

  // Fetch fresh user data on app load
  useEffect(() => {
    const fetchUser = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
          // If token is invalid, clear everything
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
          } else {
            // Fallback to stored user
            const storedUser = localStorage.getItem('user');
            if (storedUser) {
              setUser(JSON.parse(storedUser));
            }
          }
        }
      }
    };
    fetchUser();
  }, []);

  const handleLogin = (token, user) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setToken(token);
    setUser(user);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    window.location.href = '/';
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={token && !window.location.search.includes("preview") ? <Navigate to="/dashboard" /> : <LandingPage />} />
          <Route path="/auth" element={token ? <Navigate to="/dashboard" /> : <AuthPage onLogin={handleLogin} />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard" element={token ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/journal" element={token ? <DreamJournal user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/journal/edit/:editId" element={token ? <DreamJournal user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/dreams" element={token ? <DreamsLibrary user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/dreams/:id" element={token ? <DreamDetail user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/premium" element={token ? <PremiumPage user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/payment-success" element={token ? <PaymentSuccess /> : <Navigate to="/auth" />} />
          <Route path="/community-hub" element={token ? <CommunityHub user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/community" element={token ? <CommunityFeed user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/circles" element={token ? <DreamCircles user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/circles/:circleId" element={token ? <CircleDetail user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/messages" element={token ? <Messages user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/messages/:recipientId" element={token ? <Messages user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/admin" element={token && user?.is_admin ? <AdminPage user={user} onLogout={handleLogout} /> : <Navigate to="/dashboard" />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/relaxation" element={token ? <RelaxationPage user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/settings" element={token ? <SettingsPage user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/gallery" element={token ? <DreamGallery user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/my-artworks" element={token ? <MyArtworks user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
          <Route path="/games" element={token ? <GamesPage user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
      <PWAInstallPrompt />
      <AnnouncementBanner />
      <AppVersion />
      
      {/* Dream Mode Toggle - Floating button */}
      {token && (
        <button
          onClick={toggleDreamMode}
          className={`fixed bottom-32 md:bottom-6 left-4 md:left-6 p-2 md:p-3 rounded-full shadow-lg transition-all duration-300 z-40 opacity-60 hover:opacity-100 ${
            dreamMode 
              ? 'bg-indigo-600 text-yellow-300 hover:bg-indigo-700' 
              : 'bg-white/80 text-void/60 hover:bg-white hover:text-void'
          }`}
          title={dreamMode ? 'Päivätila' : 'Uni-tila'}
          data-testid="dream-mode-toggle"
        >
          {dreamMode ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5"/>
              <line x1="12" y1="1" x2="12" y2="3"/>
              <line x1="12" y1="21" x2="12" y2="23"/>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
              <line x1="1" y1="12" x2="3" y2="12"/>
              <line x1="21" y1="12" x2="23" y2="12"/>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          )}
        </button>
      )}
    </div>
  );
}

export default App;